import React from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  Description as RequestIcon,
  Insights as InsightsIcon,
  DynamicForm as FormsIcon,
  Groups as GroupsIcon,
  TableChart as TableChartIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { SEARCE_COLORS } from '../theme';

// Searce logo
const searceLogo = '/Logo_blue.jpg';

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactElement;
  requiredRoles?: string[];
  hasSubItems?: boolean;
  subItems?: MenuItem[];
}

const menuItems: MenuItem[] = [
  {
    id: 'dashboard',
    label: 'Utilization',
    icon: <DashboardIcon />,
  },
  {
    id: 'projects',
    label: 'Projects',
    icon: <AssignmentIcon />,
  },
  {
    id: 'projects-portfolio',
    label: 'Projects portfolio',
    icon: <TableChartIcon />,
  },
  {
    id: 'my-insights',
    label: 'My Insights',
    icon: <InsightsIcon />,
  },
  {
    id: 'my-team',
    label: 'My Team',
    icon: <GroupsIcon />,
  },
  {
    id: 'squad-structure',
    label: 'Squad Structure',
    icon: <PeopleIcon />,
    requiredRoles: ['admin', 'super_admin'],
  },
  {
    id: 'requests',
    label: 'Project Requests',
    icon: <RequestIcon />,
    requiredRoles: ['admin', 'super_admin'],
  },
  // {
  //   id: 'forms',
  //   label: 'Custom Forms',
  //   icon: <FormsIcon />,
  // },

  // HIDDEN MENU ITEMS - Commented out for future reference
  // These items are hidden from all users (including admin and viewer) as per requirements
  // {
  //   id: 'people-analytics',
  //   label: 'People Analytics',
  //   icon: <PeopleIcon />,
  // },
  // {
  //   id: 'business',
  //   label: 'Business Units',
  //   icon: <BusinessIcon />,
  // },
  // {
  //   id: 'schedule',
  //   label: 'Schedule',
  //   icon: <ScheduleIcon />,
  // },
  // {
  //   id: 'profile',
  //   label: 'Profile',
  //   icon: <PersonIcon />,
  // },
  {
    id: 'admin',
    label: 'Administration',
    icon: <AdminIcon />,
    requiredRoles: ['admin', 'super_admin'],
  },
];

const Sidebar: React.FC<SidebarProps> = ({ open, onToggle, activeTab, onTabChange }) => {
  const { user, logout, hasAnyRole } = useAuth();

  const handleItemClick = (itemId: string) => {
    onTabChange(itemId);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleMouseEnter = () => {
    if (!open) {
      onToggle();
    }
  };

  const handleMouseLeave = () => {
    if (open) {
      onToggle();
    }
  };

  // Get user initials for avatar
  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: 64,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? 280 : 64,
          backgroundColor: SEARCE_COLORS.sidebarBg,
          color: SEARCE_COLORS.white,
          border: 'none',
          borderRight: `1px solid ${SEARCE_COLORS.primaryDark}`,
          transition: 'width 0.3s ease-in-out',
          overflowX: 'hidden',
          position: 'fixed',
          zIndex: 1300,
          height: '100vh',
          boxShadow: '2px 0 8px rgba(0, 100, 255, 0.15)',
        },
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box sx={{ p: 2, minHeight: 64 }}>
        {/* Logo Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, minHeight: 40, justifyContent: open ? 'flex-start' : 'center' }}>
          <Box
            component="img"
            src={searceLogo}
            alt="Searce Logo"
            sx={{
              height: open ? 40 : 32,
              width: 'auto',
              maxWidth: open ? 140 : 32,
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* User Profile Section */}
        {user && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, minHeight: 40, position: 'relative' }}>
              <Avatar
                src={user.picture}
                onDoubleClick={!open ? handleLogout : undefined}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: SEARCE_COLORS.white,
                  mr: open ? 2 : 0,
                  flexShrink: 0,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: !open ? 'pointer' : 'default',
                  '&:hover': !open ? {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  } : {},
                }}
              >
                {!user.picture && getUserInitials(user.name)}
              </Avatar>
              {open && (
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: SEARCE_COLORS.white,
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '140px',
                    }}
                  >
                    {user.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.75rem',
                      lineHeight: 1.2,
                      textTransform: 'capitalize',
                      wordBreak: 'break-word',
                    }}
                  >
                    {user.role?.display_name || user.role?.name || 'User'}
                  </Typography>
                </Box>
              )}
              {open && (
                <IconButton
                  onClick={handleLogout}
                  size="small"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': {
                      color: SEARCE_COLORS.white,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    ml: 1,
                  }}
                  title="Logout"
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
              )}
              {!open && (
                <Tooltip title={`${user.name} - Double-click avatar to logout`} placement="right">
                  <Box />
                </Tooltip>
              )}
            </Box>
          </Box>
        )}

        <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', mb: 2 }} />

        {/* Navigation Menu */}
        <List sx={{ p: 0 }}>
          {menuItems
            .filter(item => {
              // Check role-based access first
              if (item.requiredRoles && !hasAnyRole(item.requiredRoles)) {
                return false;
              }

              // Special case: Exclude Leadership role from admin access
              if (item.id === 'admin' && user?.role?.name === 'leadership') {
                return false;
              }

              return true;
            })
            .map((item) => (
              <ListItem key={item.id} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => handleItemClick(item.id)}
                  sx={{
                    minHeight: 48,
                    px: open ? 2 : 1,
                    borderRadius: '8px',
                    mx: open ? 1 : 0.5,
                    backgroundColor: activeTab === item.id ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                    '&:hover': {
                      backgroundColor: activeTab === item.id ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      },
                    },
                    display: 'flex',
                    justifyContent: open ? 'flex-start' : 'center',
                    alignItems: 'center',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 2 : 0,
                      color: SEARCE_COLORS.white,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {open && (
                    <ListItemText
                      primary={item.label}
                      sx={{
                        '& .MuiListItemText-primary': {
                          color: SEARCE_COLORS.white,
                          fontWeight: activeTab === item.id ? 600 : 400,
                          fontSize: '0.9rem',
                          whiteSpace: 'nowrap',
                          lineHeight: 1.2,
                        },
                      }}
                    />
                  )}
                  {!open && (
                    <Tooltip title={item.label} placement="right">
                      <Box />
                    </Tooltip>
                  )}
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar; 